
import {refCount, publishReplay, tap} from 'rxjs/operators';
// Angular
import { Injectable } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';

// Libraries
import { Observable } from 'rxjs';

import _get from 'lodash-es/get';

// Afidium
import { Config } from '../config';
import { IQuantity } from 'libraries/service/resources/hotel/ISearchParameters';
import { ConfigurationResourceService } from 'libraries/service/resources/configuration/configurationResource.service';
import { IRepartition, ITravellerRepartition, IRepartitionTravellers } from 'libraries/service/resources/configuration/ITravellerRepartitionConfig';
import { ITagConfig } from 'libraries/service/resources/configuration/ITagConfig';
import { ITraveller } from "libraries/interfaces/ISearchResponse";

@Injectable()
export class SearchHotelFormService {

    public travellerInformations: {
        adult: any;
        child: any;
        infant: any;
    };
    private config: Config = new Config();
    private repartition: ITravellerRepartition = {}; // TODO interface

    constructor(
        private configurationResourceService: ConfigurationResourceService
    ) { }

    public getAppDefault(): Observable<any> {
        return this.getFormHotelData().pipe(
            tap((res) => {
                this.config = {
                    ...this.config,
                    segment: {
                        ...this.config.segment,
                        night: _get(res, ["quantity", "night", "default"], 1),
                    }
                };
            }),
            publishReplay(1),
            refCount(),);
    }

    public getFormHotelData(): Observable<{}> {
        return this.configurationResourceService.get(
            getPath('formHotel'), {}
        );
    }

    public getRepartitionData(): Observable<ITravellerRepartition> {
        return this.configurationResourceService.get<ITravellerRepartition>(
            getPath('repartition'), {}
        );
    }

    public getTagsData(): Observable<ITagConfig> {
        return this.configurationResourceService.get(
            getPath('tags'), {}
        );
    }

    public getTravellers(
        room: { [type: string]: number },
        travellers: { [key: string]: { type: string } }
    ): string[] {
        return Object.entries(room)
            .map(([type, number]: [string, number]) => this.getTravellersOfType(type, number, travellers))
            .reduce((aggregator: string[], typeIds: string[]) => aggregator.concat(typeIds), []);
    }

    public getTravellersOfType(type: string, number: number, travellers: { [key: string]: { type: string } }): string[] {
        if (!number) {
            return [];
        }

        const lowerCaseTye = type.toLowerCase();

        return Object.entries(travellers)
            .filter(([id, traveller]: [string, { type: string }]) => traveller.type.toLowerCase() === lowerCaseTye)
            .map(([id, traveller]: [string, { type: string }]) => id)
            .slice(0, number);
    }

    public createRoomingObject(values, form): { rooming: { [key: string]: { traveller: string[] } } } {
        const rooming = values.rooming;

        const travellers = { ...this.createTravellersObject(values, form) };
        const formattedRooming = {};

        rooming.forEach((room, index: number) => {
            const selectedTravellersIds = this.getTravellers(room, travellers);
            selectedTravellersIds.forEach((id: string) => {
                delete travellers[id];
            });

            formattedRooming['R' + (index + 1)] = {
                traveller: selectedTravellersIds,
            };
        });

        return { rooming: { ...formattedRooming } };
    }

    // Return the number of traveller
    public getTraveller(traveller): {
        adult: number;
        child: number;
        infant: number;
    } {
        let travellerList: any = {};

        const values = Object.values(traveller);

        travellerList = {
            adult: values.filter((val: any) => val.type.toLowerCase() === 'adult').length,
            child: values.filter((val: any) => val.type.toLowerCase() === 'child').length,
            infant: values.filter((val: any) => val.type.toLowerCase() === 'infant').length,
        };

        return travellerList;
    }

    public getRepartition(quantities: IQuantity, repartition: ITravellerRepartition, travellers: ITraveller[]): IRepartition {
        const formattedQuantities = this.transformFormQuantitiesToJsonFormat(quantities);
        return (!repartition) ?
            this.repartition[formattedQuantities] :
            repartition[formattedQuantities];
    }

    public getTravellersByRoom(rooming) {
        const travellers = [];

        Object.keys(rooming).forEach((room) => {
            travellers.push(this.getTraveller(rooming[room].traveller));
        });

        return travellers;
    }

    public transformFormQuantitiesToJsonFormat(quantities: IQuantity): string {
        let formattedQuantities = 'ADT_1';

        Object.keys(quantities).forEach((quantity) => {
            if (quantity === 'adult') {
                formattedQuantities = 'ADT_' + quantities.adult;
            } else if (quantity === 'child' && quantities.child > 0) {
                const hasPlus = quantities.adult > 0 ? '+' : '';
                formattedQuantities += hasPlus + 'CHD_' + quantities.child;
            } else if (quantity === 'infant' && quantities.infant > 0) {
                const hasPlus = quantities.child > 0 || (quantities.child === 0 && quantities.adult > 0) ? '+' : '';
                formattedQuantities += hasPlus + 'INF_' + quantities.infant;
            }
        });

        return formattedQuantities;
    }

    public createTravellersObject(values, form: FormGroup): any { // TODO Interface
        const adults = values.quantity.adult;

        const total = getTotalTravellers(form);
        const travellers = {};
        let travellersArray = [];

        let count = 1;

        while (count <= adults) {
            travellers['T_' + count] = {
                id: 'T_' + count,
                type: 'Adult'
            };

            travellersArray.push({
                type: 'Adult'
            });

            count++;
        }

        travellersArray = [...travellersArray, ...values.child, ...values.infant];

        travellersArray.forEach((traveller, index: number) => {
            travellers['T_' + (index + 1)] = {
                ...traveller,
                id: 'T_' + (index + 1),
            };
        });

        return travellers;
    }
}

function getPath(type: string = 'formHotel'): string[] {
    switch (type) {
        case 'repartition':
            return ['wui', 'shared_hotel', 'config', 'forms', 'repartition.json'];
        case 'formHotel':
            return ['wui', 'shared_hotel', 'config', 'forms', 'form_hotel.json'];
        case 'tags':
            return ['wui', 'shared_hotel', 'config', 'forms', 'tags.json'];
        default:
            return null;
    }
}

function getTotalTravellers(form: FormGroup): number {
    const quantity: AbstractControl = form.get('quantity');
    const adults: number = quantity.get('adult').value;
    const children: number = quantity.get('child').value;
    const infants: number = quantity.get('infant').value;

    return adults + children + infants;
}
